import Env from 'root/lib/Env';

import { aquaAxios } from './utilityFunctions';
import { DEFAULT_COUNTRY } from './constants';

const { GAME_CONTENT_URL } = Env;
const LOCALIZATION_MANIFEST_URL = `${GAME_CONTENT_URL}/LOCALIZATION_MANIFEST.mf`;
const MANIFEST_URL = `${GAME_CONTENT_URL}/MANIFEST.mf`;

const localizationManifest = new Set();
const manifest = new Set();

const fetchManifest = async (url, targetSet) => {
  try {
    const { data } = await aquaAxios(url);
    if (data) {
      data.split('\n').forEach((line) => targetSet.add(line));
    }
  } catch {
    console.warn(`StaticAssetsManifest.js failed to fetch manifest: ${url}`);
  }
};

/**
 * Fetches the static asset manifests and populates the
 * in-memory lookup sets used by related helpers.
 * The localization manifest is always fetched; the full asset
 * manifest is fetched only in dev environments (used by the debugging helpers).
 *
 * @returns {Promise<void>} Resolves once all manifest fetches have settled.
 */
export const fetchStaticAssetsManifest = async () => {
  const fetches = [fetchManifest(LOCALIZATION_MANIFEST_URL, localizationManifest)];

  if (Env.isDevEnv) {
    fetches.push(fetchManifest(MANIFEST_URL, manifest));
  }

  await Promise.allSettled(fetches);
};

const verifyAssetOnRemote = path => manifest.has(`./${path}`);
export const verifyLocalizedAssetOnRemote = path => localizationManifest.has(`./${path}`);

export const getValidatedCountryCode = ({ file, path, countryCode }) => (
  countryCode !== DEFAULT_COUNTRY && verifyLocalizedAssetOnRemote(`${path}${countryCode}/${file}`)
    ? countryCode
    : DEFAULT_COUNTRY);

export const verifyGameLanguage = (gameId, countryCode) =>
  [...localizationManifest].some(key => new RegExp(`\\./${gameId}/(.+)/${countryCode}/(.+)`).test(key));

// Asset verification debugging (only for Env.isDevEnv)

const verifyCurriculumMap = (curriculumKeys, map) => {
  const missingFromMap = new Set();

  Object.entries(curriculumKeys).forEach(([level, assets]) => {
    assets.forEach((asset) => {
      const assetData = map[level][asset];
      if (!assetData) {
        missingFromMap.add(`${level}:${asset}`);
      }
    });
  });

  return missingFromMap;
};

const verifyMapAssets = (map, countryCode) => {
  const isDefaultCountryCode = countryCode === DEFAULT_COUNTRY;
  const missingFiles = new Set();
  const fallbackFiles = new Set();

  Object.entries(map).forEach(([level, assets]) => {
    Object.entries(assets).forEach(([assetKey, assetData]) => {
      const isDefaultAvailable = verifyAssetOnRemote(`${assetData.path}${DEFAULT_COUNTRY}/${assetData.file}`);
      if (!isDefaultCountryCode) {
        const isLocalized = verifyLocalizedAssetOnRemote(`${assetData.path}${countryCode}/${assetData.file}`);
        if (!isLocalized) {
          if (isDefaultAvailable) {
            fallbackFiles.add(`${level}:${assetKey}`);
          } else {
            missingFiles.add(`${level}:${assetKey}`);
          }
        }
      } else if (!isDefaultAvailable) {
        missingFiles.add(`${level}:${assetKey}:${assetData.file}`);
      }
    });
  });

  return { missingFiles, fallbackFiles };
};

export const verifyAssets = (countryCode, curriculumKeys, audioMap, imageMap) => {
  const missingAssets = {
    missingAudiomapFromCurriculum: verifyCurriculumMap(curriculumKeys, audioMap),
    missingAudioAssets: verifyMapAssets(audioMap, countryCode),
  };
  if (imageMap) {
    missingAssets.missingImagemapFromCurriculum = verifyCurriculumMap(curriculumKeys, imageMap);
    missingAssets.missingImageAssets = verifyMapAssets(imageMap, countryCode);
  }
  console.log('Missing Assets', missingAssets);
};
